import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import PlatformBadge from './PlatformBadge';
import { LiveGames } from './Possession';
import { ticketLabel } from './SharedPicks';
import { isVoid, uploadedAt, missingLegs, offDayLegs, sortedLegs, blinkClass, groupByWindow } from './ticketDate';

const DOT = { green: 'bg-emerald-400', blue: 'bg-sky-400', purple: 'bg-purple-400', yellow: 'bg-transparent border border-yellow-400', orange: 'bg-orange-500', red: 'bg-red-500', neutral: 'bg-zinc-600' };
const colorOf = (l) => (l.performance_status === 'LIVE_TIED' ? 'yellow' : l.color_token || 'neutral');
const usd = (n) => (n ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—');

// Ticket state + a performance score: won on top, then most legs hit/winning, lost at the bottom.
export const tileState = (t) => {
  const legs = t.legs || [];
  const c = legs.map(colorOf);
  const n = c.filter((x, i) => !isVoid(legs[i])).length || 1;
  const won = c.filter((x) => x === 'green').length;
  const lost = t.manual_result ? t.manual_result === 'lost' : c.includes('red') || t.status === 'lost';
  const allHit = t.manual_result ? t.manual_result === 'won' : !lost && !missingLegs(t) && (t.status === 'won' || (won > 0 && won + legs.filter(isVoid).length === legs.length));
  const score = allHit ? 1000 : lost ? -1000 + won : (100 * (won + 0.6 * c.filter((x) => x === 'blue' || x === 'purple').length + 0.25 * c.filter((x) => x === 'yellow').length)) / n;
  return { won, lost, allHit, score, colors: c };
};

const Tile = ({ t, onOpen, onChanged, picked }) => {
  const s = tileState(t);
  const ring = picked ? 'border-red-400 bg-red-500/20 ring-2 ring-red-400' : s.lost ? 'border-red-500/50 bg-red-950/30' : s.allHit ? 'border-emerald-400/70 bg-emerald-950/30' : 'border-zinc-700 bg-zinc-900/70';
  return (
    <div role="button" tabIndex={0} className={`relative rounded-lg border p-1.5 pt-2 min-w-0 cursor-pointer transition-transform hover:-translate-y-0.5 ${ring}`}
      onClick={() => onOpen(t)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t); }} data-testid={`tile-${t.id}`}>
      {picked !== undefined ? (
        <span className={`absolute top-0.5 right-0.5 w-5 h-5 rounded border flex items-center justify-center text-[11px] font-black ${picked ? 'bg-red-500 border-red-300 text-white' : 'border-zinc-500 bg-zinc-900'}`} data-testid={`tile-check-${t.id}`}>{picked ? '✓' : ''}</span>
      ) : <PlatformBadge ticket={t} onChanged={onChanged} className="!w-5 !h-5 !text-[10px] !top-0.5 !right-0.5" />}
      <div className="pr-5"><LiveGames ticket={t} max={2} /></div>
      <p className="text-[10px] font-bold text-white truncate pr-5" title={ticketLabel(t)}>{ticketLabel(t).replace(/^\$[\d,.]+\s*/, '')}</p>
      <p className="text-[10px] text-zinc-400 truncate">{usd(t.stake)} → <span className="text-emerald-300 font-semibold">{usd(t.potential_payout)}</span></p>
      <p className={`text-[10px] font-bold ${s.lost ? 'text-red-400' : s.allHit ? 'text-emerald-300' : 'text-sky-300'}`} data-testid={`tile-status-${t.id}`}>
        {s.lost ? 'LOST' : s.allHit ? 'WON' : missingLegs(t) && t.status === 'needs_settlement' ? 'Check legs' : 'Alive'}{t.manual_result ? ' (marked)' : ` · ${s.won}/${(t.legs || []).length}`}
      </p>
      {Object.keys(offDayLegs(t)).length > 0 && <p className="text-[8px] font-bold text-amber-300 truncate" data-testid={`tile-offday-${t.id}`}>⚠ {Object.values(offDayLegs(t)).join(', ')} game</p>}
      {(t.legs || []).some((l) => l.foul_trouble && !String(l.performance_status || '').startsWith('FINAL')) && (
        <p className="text-[8px] font-black text-orange-300 truncate" data-testid={`tile-fouls-${t.id}`}>
          ⚠ Fouls: {(t.legs || []).filter((l) => l.foul_trouble && !String(l.performance_status || '').startsWith('FINAL')).map((l) => `${(l.player_name || '').split(' ').slice(-1)[0]} ${l.fouls}`).join(', ')}
        </p>
      )}
      <p className="text-[8px] text-zinc-500 truncate" data-testid={`tile-uploaded-${t.id}`}>{uploadedAt(t).replace('Uploaded ', 'Up ')}</p>
      <div className="flex flex-wrap gap-[3px] mt-1" data-testid={`tile-dots-${t.id}`}>
        {sortedLegs(t.legs || []).map(([l, i]) => (
          <span key={l.id || i} title={l.selection} className={`w-2 h-2 rounded-full ${l.availability ? 'bg-orange-500' : DOT[s.colors[i]] || DOT.neutral}${blinkClass(l.blink, s.colors[i])}`} />
        ))}
      </div>
    </div>
  );
};

export default function TicketTiles({ tickets, onChanged, renderFull, selected, onToggle }) {
  const [openId, setOpenId] = useState(null);
  const days = useMemo(() => groupByWindow(tickets).map((g) => ({ k: g.key, label: g.label, list: g.list })), [tickets]);
  const open = tickets.find((t) => t.id === openId);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setOpenId(null); };
    document.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [open]);
  return (
    <div className="space-y-3" data-testid="ticket-tiles">
      {days.map(({ k, label, list }) => (
        <div key={k}>
          <p className="text-xs font-semibold text-zinc-300 mb-1.5 px-1" data-testid={`tiles-day-${k}`}>{label} · {list.length} ticket{list.length === 1 ? '' : 's'}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8 gap-1.5">
            {list.map((t) => <Tile key={t.id} t={t} onChanged={onChanged} picked={selected ? selected.has(t.id) : undefined} onOpen={(x) => (selected ? onToggle(x.id) : setOpenId(x.id))} />)}
          </div>
        </div>
      ))}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-3" onClick={() => setOpenId(null)} data-testid="tile-modal">
          <div className="max-w-xl mx-auto mt-8 relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setOpenId(null)} data-testid="tile-modal-close"
              className="absolute -top-8 right-0 rounded-full bg-zinc-800 p-1.5 text-zinc-200 hover:bg-zinc-700"><X className="w-4 h-4" /></button>
            {renderFull(open)}
          </div>
        </div>
      )}
    </div>
  );
}
